import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import type { ServiceAgreementDTO } from '@invenco/common-interface/accounts';
import { LocationType } from '@invenco/common-interface/accounts';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { SelectItem } from '@/components/core/select';
import { useWhileDialogOpen } from '@/components/dialog-with-context';
import { DatePickerFormField } from '@/components/forms/date-picker-form-field';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { SelectFormField } from '@/components/forms/select-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import { LocalCalendarDate } from '@/shared/domain/LocalCalendarDate';
import { Result } from '@/shared/helpers/Result';

type FormValues = {
  locationId: string;
  commencementDate?: Date;
  terminationDate?: Date;
  name?: string;
};

type Props = {
  accountId: string;
  mode: 'add' | 'edit' | 'terminate';
  serviceAgreement?: Partial<ServiceAgreementDTO>;
  onAdd: (data: { locationId: string; commencementDate: string }) => Promise<Result>;
  onUpdate: (data: { id: string; commencementDate: string }) => Promise<Result>;
  onTerminate: (data: { id: string; terminationDate: string }) => Promise<Result>;
};

const buildSchema = (mode: Props['mode']) =>
  z
    .object({
      locationId: z.string().min(1, 'Select a location'),
      commencementDate: z.date().optional(),
      terminationDate: z.date().optional(),
      name: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.commencementDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Select a commencement date',
          path: ['commencementDate'],
        });
      }

      if (mode === 'terminate' && !data.terminationDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Select a termination date',
          path: ['terminationDate'],
        });
      }
    });

const dialogTitleByMode: Record<Props['mode'], string> = {
  add: 'Add service agreement',
  edit: 'Edit service agreement',
  terminate: 'Terminate service agreement',
};

const dialogDescriptionByMode: Record<Props['mode'], string> = {
  add: 'Select the managed location and commencement date.',
  edit: 'Update the commencement date for this service agreement.',
  terminate: 'Select the termination date for this service agreement.',
};

export function ServiceAgreementDialogContent({
  accountId,
  mode,
  serviceAgreement,
  onAdd,
  onUpdate,
  onTerminate,
}: Props) {
  const { accountsGateway } = useGateways();
  const stableMode = useWhileDialogOpen(mode);
  const stableServiceAgreement = useWhileDialogOpen(serviceAgreement);
  const schema = useMemo(() => buildSchema(stableMode), [stableMode]);

  const defaultValues = useMemo<FormValues>(
    () => ({
      locationId: stableServiceAgreement?.locationId ?? '',
      commencementDate: stableServiceAgreement?.commencementDate
        ? LocalCalendarDate.fromISODate(stableServiceAgreement.commencementDate).toJSDate()
        : undefined,
      terminationDate: stableServiceAgreement?.terminationDate
        ? LocalCalendarDate.fromISODate(stableServiceAgreement.terminationDate).toJSDate()
        : undefined,
      name: stableServiceAgreement?.name ?? '',
    }),
    [
      stableServiceAgreement?.commencementDate,
      stableServiceAgreement?.locationId,
      stableServiceAgreement?.name,
      stableServiceAgreement?.terminationDate,
    ],
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const { data: locations, isLoading: isLoadingLocations } = useQuery({
    queryKey: ['locations'],
    queryFn: ({ signal }) => accountsGateway.getLocations(undefined, { signal }),
    select: ({ items }) =>
      items.filter(
        (location) => location.type === LocationType.MANAGED && location.accountId === accountId,
      ),
  });

  const dialogTitle = dialogTitleByMode[stableMode];
  const dialogDescription = dialogDescriptionByMode[stableMode];
  const submitLabel = stableMode === 'terminate' ? 'Terminate' : 'Save';

  return (
    <FormDialogContent
      form={form}
      submitLabel={submitLabel}
      onSubmit={async (data) => {
        if (stableMode === 'terminate') {
          if (!stableServiceAgreement?.id || !data.terminationDate) {
            return Result.fail('Required service agreement details missing');
          }
          return onTerminate({
            id: stableServiceAgreement.id,
            terminationDate: LocalCalendarDate.fromJSDate(data.terminationDate).toISODate(),
          });
        }

        if (stableMode === 'edit') {
          if (!stableServiceAgreement?.id || !data.commencementDate) {
            return Result.fail('Required service agreement details missing');
          }
          return onUpdate({
            id: stableServiceAgreement.id,
            commencementDate: LocalCalendarDate.fromJSDate(data.commencementDate).toISODate(),
          });
        }

        if (!data.commencementDate || !data.locationId) {
          return Result.fail('Required service agreement details missing');
        }
        return onAdd({
          locationId: data.locationId,
          commencementDate: LocalCalendarDate.fromJSDate(data.commencementDate).toISODate(),
        });
      }}
    >
      <DialogHeader>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogDescription>{dialogDescription}</DialogDescription>
      </DialogHeader>
      <DialogBody>
        {stableServiceAgreement?.id ? (
          <TextFormField control={form.control} name="name" label="Name" disabled />
        ) : null}
        <SelectFormField
          control={form.control}
          name="locationId"
          label="Location"
          placeholder={isLoadingLocations ? 'Loading locations...' : 'Select a location'}
          disabled={isLoadingLocations || Boolean(stableServiceAgreement?.id)}
          className="w-full"
        >
          {locations?.length
            ? locations.map((location) => (
                <SelectItem key={location.id} value={location.id}>
                  {location.name}
                </SelectItem>
              ))
            : null}
          {!locations?.length && !isLoadingLocations ? (
            <SelectItem value="no-locations" disabled>
              No managed locations
            </SelectItem>
          ) : null}
        </SelectFormField>
        <DatePickerFormField
          control={form.control}
          name="commencementDate"
          label="Commencement date"
          disabled={stableMode === 'terminate'}
        />
        {stableMode === 'terminate' ? (
          <DatePickerFormField
            control={form.control}
            name="terminationDate"
            label="Termination date"
          />
        ) : null}
      </DialogBody>
    </FormDialogContent>
  );
}
